import { bootstrapApplication } from '@angular/platform-browser';

import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';
import { AppRuntimeConfig } from './app/core/auth/app-config.model';
import { provideAuth } from './app/core/auth/auth.providers';

/**
 * Runtime config is fetched BEFORE bootstrap so the same build/Docker image can
 * point at any Keycloak realm and API by just swapping assets/config.json on the
 * host. provideAuth() then initializes Keycloak (login-required) as part of app
 * startup, so an unauthenticated user is redirected to login before the UI loads.
 */
// Root-absolute path: on a deep-link hard refresh (e.g. /projects/123) a relative
// path would resolve under that route and the SPA fallback would return index.html.
//
// WHERE THIS FILE COMES FROM differs by environment, and the difference has bitten
// us: in `ng serve` it is src/assets/config.json, but in the container
// docker/30-render-config.sh OVERWRITES it at startup from the service's env vars.
// So editing src/assets/config.json changes local development and nothing else —
// a deployed app points wherever its compose file's API_URL says.
fetch('/assets/config.json', { cache: 'no-store' })
  .then((res) => {
    if (!res.ok) throw new Error(`config.json ${res.status}`);
    return res.json() as Promise<AppRuntimeConfig>;
  })
  .then((runtimeConfig) =>
    bootstrapApplication(AppComponent, {
      ...appConfig,
      providers: [...appConfig.providers, provideAuth(runtimeConfig)],
    }),
  )
  .then(() => sessionStorage.removeItem(RETRY_FLAG))
  .catch((err: unknown) => {
    // Bootstrap/config failures are fatal and must never be swallowed silently.
    console.error('Failed to bootstrap DROS', err);
    recoverOrReport(err);
  });

/** Set while a recovery reload is in flight, so one failure cannot become a loop. */
const RETRY_FLAG = 'dros.authRetry';

/**
 * Try once to recover from a failed start, then say what happened.
 *
 * WHY: the usual cause is a stale Keycloak artefact in this browser. keycloak-js
 * writes a `kc-callback-<state>` entry before redirecting to the login page; if
 * that round trip is interrupted — the session idles out, the tab is left
 * overnight, the redirect is abandoned — the entry survives and the NEXT start
 * fails validating a state that no longer means anything.
 *
 * Which is exactly why a private window works: it has none of them. Clearing them
 * and reloading once does the same thing without making anybody open one.
 *
 * The flag stops a genuine outage from becoming a reload loop: a second failure
 * reports instead of retrying. It is cleared on the next successful start.
 */
function recoverOrReport(err: unknown): void {
  if (!sessionStorage.getItem(RETRY_FLAG)) {
    sessionStorage.setItem(RETRY_FLAG, '1');
    clearStaleAuthState();
    // replace(), not reload(): drops any `?state=…&code=…` left on the URL by the
    // interrupted round trip, which would otherwise be re-validated and fail again.
    window.location.replace(window.location.origin + window.location.pathname);
    return;
  }
  sessionStorage.removeItem(RETRY_FLAG);
  report(err);
}

/** Remove keycloak-js's own client-side crumbs. Nothing else is touched. */
function clearStaleAuthState(): void {
  for (const store of [window.localStorage, window.sessionStorage]) {
    try {
      for (const key of Object.keys(store)) {
        if (key.startsWith('kc-callback')) store.removeItem(key);
      }
    } catch {
      // A browser with storage blocked has nothing stale to clear.
    }
  }
}

/**
 * Say something, rather than leave a white page.
 *
 * Written without Angular on purpose: this runs when Angular did not start.
 */
function report(err: unknown): void {
  const detail = err instanceof Error ? err.message : String(err);
  document.body.innerHTML = `
    <div style="font:14px system-ui,sans-serif;max-width:34rem;margin:12vh auto;padding:0 1.5rem;color:#0f172a">
      <h1 style="font-size:1.25rem;margin:0 0 .5rem">DROS could not start</h1>
      <p style="margin:0 0 1rem;line-height:1.5">
        This is usually a sign-in that was interrupted. Reloading clears it; if it
        keeps happening, the identity server or the app's configuration is down.
      </p>
      <p style="margin:0 0 1.25rem;color:#64748b;font-family:ui-monospace,monospace;font-size:12px">${escapeHtml(detail)}</p>
      <button onclick="location.replace(location.origin + location.pathname)"
              style="font:inherit;padding:.5rem 1rem;border:1px solid #1e293b;background:#1e293b;color:#fff;cursor:pointer">
        Try again
      </button>
    </div>`;
}

function escapeHtml(value: string): string {
  const el = document.createElement('div');
  el.textContent = value;
  return el.innerHTML;
}
